import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Award, CheckCircle, XCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useAuth } from "../App";
import { toast } from "sonner";
import { api } from "../services/apiClient";

const AdminBrokers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      const response = await api.get("/brokers", { auth: false });

      let data = null;
      try {
        data = await response.json();
      } catch (_parseError) {
        data = null;
      }

      if (!response.ok) {
        const detail = String(data?.detail || data?.message || "").toLowerCase();
        const isNoData =
          response.status === 404 ||
          ((detail.includes("no hay") || detail.includes("sin")) &&
            (detail.includes("broker") || detail.includes("datos")));

        if (isNoData) {
          setBrokers([]);
          return;
        }

        throw new Error(data?.detail || data?.message || "Error al cargar brokers");
      }

      setBrokers(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.message || "Error al cargar brokers");
    } finally {
      setLoading(false);
    }
  };

  const toggleVerify = async (brokerId, currentStatus) => {
    try {
      await api.patchQuery(`/brokers/${brokerId}/verify`);
      toast.success(
        currentStatus ? "Verificación removida" : "Broker verificado",
      );
      fetchBrokers();
    } catch (error) {
      toast.error("Error");
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-4">
          <BackNavigationButton dataTestId="admin-brokers-back" />
        </div>
        <h1 className="text-4xl font-bold mb-8">Gestión de Brokers</h1>

        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">Todos ({brokers.length})</TabsTrigger>
            <TabsTrigger value="pending">
              Pendientes ({brokers.filter((b) => !b.is_verified).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {brokers.map((broker) => (
                <div
                  key={broker.id}
                  className="bg-background border rounded-lg p-6"
                >
                  <h3 className="font-bold mb-2">{broker.name}</h3>
                  {broker.is_verified ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      ✓ Verificado
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                      ⏳ Pendiente
                    </span>
                  )}
                  <p className="text-sm text-muted-foreground mt-3">
                    {broker.description}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant={broker.is_verified ? "outline" : "default"}
                      onClick={() =>
                        toggleVerify(broker.id, broker.is_verified)
                      }
                    >
                      {broker.is_verified ? (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          Quitar
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Verificar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pending" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {brokers
                .filter((b) => !b.is_verified)
                .map((broker) => (
                  <div
                    key={broker.id}
                    className="bg-background border-2 border-orange-200 rounded-lg p-6"
                  >
                    <h3 className="font-bold mb-2">{broker.name}</h3>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                      ⏳ Pendiente
                    </span>
                    <p className="text-sm text-muted-foreground mt-3 mb-3">
                      {broker.description}
                    </p>

                    <div className="space-y-1 text-xs mb-3">
                      {broker.years_experience > 0 && (
                        <p>📅 {broker.years_experience} años experiencia</p>
                      )}
                      {broker.license_number && (
                        <p>📜 Lic: {broker.license_number}</p>
                      )}
                      {broker.phone && <p>📱 {broker.phone}</p>}
                      {broker.website && (
                        <p>
                          🌐{" "}
                          <a
                            href={broker.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            Ver sitio
                          </a>
                        </p>
                      )}
                    </div>

                    {broker.specialties && broker.specialties.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold mb-1">
                          Especialidades:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {broker.specialties.map((s, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-muted text-xs rounded-full"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      size="sm"
                      className="w-full mt-4"
                      onClick={() => toggleVerify(broker.id, false)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verificar
                    </Button>
                  </div>
                ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminBrokers;
